import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, ChevronDown, Settings, LogOut, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import Logo from '@/components/Logo';
import NotificationBell from '@/components/NotificationBell';

export default function Header({ sidebarCollapsed, onToggleSidebar }) {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const displayName = user?.name || user?.email?.split('@')[0] || 'Guest';
  const initials = (displayName || '?').slice(0, 2).toUpperCase();
  const tier = (user?.subscription_tier || 'basic').toUpperCase();

  const handleLogout = () => {
    logout();
    toast.success(t('auth.signedOut'));
    navigate('/login', { replace: true });
  };

  return (
    <header data-testid="app-header" className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card/80 backdrop-blur-xl px-4 lg:px-6">
      <div className="flex items-center gap-3">
        <button
          data-testid="sidebar-toggle"
          onClick={onToggleSidebar}
          className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <Logo size={32} variant="full" tagline />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <LanguageSwitcher />
        <div className="flex items-center gap-2 mr-2" data-testid="theme-toggle">
          <Sun className="w-4 h-4 text-muted-foreground" />
          <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} />
          <Moon className="w-4 h-4 text-muted-foreground" />
        </div>

        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 pl-2 pr-1" data-testid="user-menu-trigger">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                {initials}
              </div>
              <span className="text-sm font-medium hidden md:inline" data-testid="header-user-name">{displayName}</span>
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="text-sm">{displayName}</span>
              <span className="text-xs text-muted-foreground font-normal truncate">{user?.email}</span>
              <span className="text-[10px] mt-1 inline-flex self-start px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold" data-testid="header-user-tier">{tier}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="user-settings-btn">
              <Settings className="w-4 h-4 mr-2" /> {t('header.settings')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings/api-keys')} data-testid="user-api-keys-btn">
              <Key className="w-4 h-4 mr-2" /> {t('header.apiKeys')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> {t('header.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
